import { useCallback, useState } from 'react';

import {
  providerDocumentSourceType,
  requireProviderDocumentSource,
  useCreateExternalDocument,
} from '@spotify/confidence-external-documents-react';
import {
  CollaborationV1ExternalDocumentSourceType,
  CollaborationV1ProviderDocumentPage,
} from '@spotify/confidence-graphql';

import { ChatAttachment, PendingAttachment } from '../../types';

export type UseAttachProviderDocResult = {
  isDialogOpen: boolean;
  openDialog: () => void;
  closeDialog: () => void;
  selectPage: (page: CollaborationV1ProviderDocumentPage) => Promise<void>;
  isAttaching: boolean;
};

const PENDING_SOURCE_TYPES: Record<
  CollaborationV1ExternalDocumentSourceType,
  PendingAttachment['sourceType']
> = {
  [CollaborationV1ExternalDocumentSourceType.GoogleDocs]: 'GOOGLE_DOCS',
  [CollaborationV1ExternalDocumentSourceType.GoogleSlides]: 'GOOGLE_SLIDES',
  [CollaborationV1ExternalDocumentSourceType.Notion]: 'NOTION',
};

const toPendingAttachment = (
  page: CollaborationV1ProviderDocumentPage,
  sourceType: CollaborationV1ExternalDocumentSourceType,
): PendingAttachment => {
  const source = requireProviderDocumentSource(page);
  return {
    kind: 'pending',
    sourceType: PENDING_SOURCE_TYPES[sourceType],
    sourceId: source.sourceId,
    title: page.title ?? 'Untitled',
  };
};

export const useAttachProviderDoc = (options: {
  chatName?: string;
  sourceType: CollaborationV1ExternalDocumentSourceType;
  onAttached: (attachment: ChatAttachment) => void;
}): UseAttachProviderDocResult => {
  const { chatName, sourceType, onAttached } = options;
  const { createExternalDocument } = useCreateExternalDocument();
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isAttaching, setIsAttaching] = useState(false);

  const openDialog = useCallback(() => setIsDialogOpen(true), []);
  const closeDialog = useCallback(() => setIsDialogOpen(false), []);

  const selectPage = useCallback(
    async (page: CollaborationV1ProviderDocumentPage) => {
      const pageSourceType = providerDocumentSourceType(sourceType, page);
      if (!chatName) {
        onAttached(toPendingAttachment(page, pageSourceType));
        return;
      }

      setIsAttaching(true);
      try {
        const ref = await createExternalDocument({
          resource: chatName,
          title: page.title ?? 'Untitled',
          source: {
            type: pageSourceType,
            ...requireProviderDocumentSource(page),
          },
        });
        onAttached({
          kind: 'resolved',
          name: ref.name,
          title: ref.title ?? page.title ?? 'Untitled',
          uri: ref.source?.uri ?? undefined,
        });
      } finally {
        setIsAttaching(false);
      }
    },
    [chatName, sourceType, createExternalDocument, onAttached],
  );

  return {
    isDialogOpen,
    openDialog,
    closeDialog,
    selectPage,
    isAttaching,
  };
};
